import { useEffect, useState, useCallback } from 'react';
import { useTestContext } from '../../../context/testContext';
import { useLogmarData, useOptotypeData } from '../../../helpers/reducers';
import { usePostMessage, useReceiveMessage } from '../../../context/messageContext';
import { useLogmarSizeCalculations } from '../../../helpers/useLogmarSizeCalculations';
import { useCalibration } from '../../../context/calibrationContext';
import { SessionActions } from '../../../ui';
import OptotypePreview from '../../../components/KaysOptotypes/OptotypePreview';
import { useKaysControls, KaysAnswerControls, KaysSizeControls } from '../../../components/KaysOptotypes/KaysControls';
import { ExamBody, ScoringGrid, Divider } from '../exams/common';
import AutoResizeControls from '../exams/AutoResizeControls';

const KaysOptotypesTest = ({ eyeTested, updateStep }) => {
  const [valuesInitialised, setValuesInitialised] = useState(false);

  const postMessage = usePostMessage();
  const receivedMessage = useReceiveMessage();
  const { getContentSize } = useLogmarSizeCalculations();
  const {
    calibrationData: { distanceFromScreen, methodSelected },
  } = useCalibration();
  const {
    testData: { optotypesToShow, kaysCrowding, testMode, [eyeTested]: initialLogmarSize, testLeftEye },
    updateTestData,
  } = useTestContext();
  const {
    currentLogmarData: { logmarSize, finalScore },
    updateLogmarData,
  } = useLogmarData();
  const {
    currentOptotypeData: { currentOptotypes, highlightedOptotype },
    updateOptotypeData,
  } = useOptotypeData();

  useEffect(() => {
    updateTestData({ testInProgress: true });
  }, [updateTestData]);

  const getRandomOptotypes = useCallback(() => {
    const optotypeKeys = ['apple', 'boot', 'car', 'duck', 'house', 'star'];
    const desiredAmount = optotypesToShow;
    const randomOptotypes = optotypeKeys.sort(() => 0.5 - Math.random()).slice(0, desiredAmount);
    if (randomOptotypes[0] === currentOptotypes?.[0]) return getRandomOptotypes();
    return postMessage({ type: 'useOptotypeReducer', action: 'SET_CURRENT_OPTOTYPES', payload: randomOptotypes });
  }, [optotypesToShow, postMessage, currentOptotypes]);

  const finishTest = () => {
    updateTestData({ [eyeTested]: { score: finalScore } });
    const nextStep = eyeTested === 'rightEye' && testLeftEye ? 'leftEyeSetup' : 'results';
    return updateStep(nextStep);
  };

  useEffect(() => {
    //this receives "reducer" messages and dispatches the appropriate action.
    if (receivedMessage.type === 'useOptotypeReducer') {
      updateOptotypeData({ type: receivedMessage.action, payload: receivedMessage.payload });
    }
    if (receivedMessage.type === 'useLogmarReducer') {
      updateLogmarData({ type: receivedMessage.action, payload: receivedMessage.payload });
    }
  }, [receivedMessage, updateOptotypeData, updateLogmarData]);

  useEffect(() => {
    if (!initialLogmarSize || valuesInitialised) return;
    //initialises values on component load and sends start msg to patient
    postMessage({
      type: 'kaysMultiple',
      content: {
        distanceFromScreen: distanceFromScreen,
        methodSelected: methodSelected,
        testMode: methodSelected !== 'manual' ? 'auto' : testMode,
      },
    });
    postMessage({ type: 'useLogmarReducer', action: 'setInitialLogmarSize', payload: initialLogmarSize });
    postMessage({ type: 'useOptotypeReducer', action: 'SET_OPTOTYPE_CROWDING', payload: kaysCrowding === 'crowded' });
    getRandomOptotypes();
    return setValuesInitialised(true);
  }, [getRandomOptotypes, postMessage, initialLogmarSize, kaysCrowding, distanceFromScreen, methodSelected, testMode, valuesInitialised]);

  useEffect(() => {
    //updates size for patient.
    const newSize = Math.round(getContentSize(logmarSize.id, distanceFromScreen));
    postMessage({ type: 'useOptotypeReducer', action: 'SET_OPTOTYPE_SIZE', payload: newSize });
  }, [distanceFromScreen, logmarSize.id, postMessage, getContentSize]);

  const controls = useKaysControls({ getRandomOptotypes, finishTest, logmarSize, eyeTested, updateStep });

  return (
    <ExamBody style={{ gap: 16 }}>
      <ScoringGrid left='1.25fr' right='1fr' align='center'>
        <OptotypePreview currentOptotypes={currentOptotypes} highlightedOptotype={highlightedOptotype} />
        <KaysAnswerControls controls={controls} />
      </ScoringGrid>
      <Divider />
      <ScoringGrid left='1.25fr' right='1fr' align='end' style={{ flex: '0 0 auto' }}>
        <AutoResizeControls />
        <KaysSizeControls controls={controls} logmarValue={logmarSize?.value?.toFixed(1)} />
      </ScoringGrid>
      <SessionActions onBack={controls.back} />
    </ExamBody>
  );
};

export default KaysOptotypesTest;
